import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, Lock, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";

interface ResultAccessGateProps {
  studentId: string;
  semester?: string | null;
  studentClass?: string | null;
  examType?: string | null;
  children: React.ReactNode;
}

export function ResultAccessGate({ studentId, semester, studentClass, examType, children }: ResultAccessGateProps) {
  const { data: access, isLoading } = useQuery({
    queryKey: ["result-access", studentId, semester, studentClass, examType],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("can_student_view_results", {
        _student_id: studentId,
        _semester: semester ?? null,
        _class: studentClass ?? null,
        _exam_type: examType ?? null,
      });
      if (error) throw error;
      return data?.[0] ?? { allowed: false, reason: "Unable to check access." };
    },
    enabled: !!studentId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-muted rounded w-3/4 mx-auto" />
            <div className="h-3 bg-muted rounded w-1/2 mx-auto" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!access?.allowed) {
    const reason = access?.reason ?? "";
    const isFeeIssue = reason.toLowerCase().includes("fees") || reason.toLowerCase().includes("finance");
    const isRestricted = reason.toLowerCase().includes("restricted");
    const Icon = isFeeIssue ? CreditCard : isRestricted ? Lock : AlertCircle;
    const title = isFeeIssue
      ? "Financial Clearance Required"
      : isRestricted
      ? "Results Restricted"
      : "Results Not Available";

    return (
      <Card className={cn(
        "border-l-4",
        isFeeIssue ? "border-l-amber-500 bg-amber-500/5" : isRestricted ? "border-l-destructive bg-destructive/5" : "border-l-muted-foreground bg-muted/30"
      )}>
        <CardContent className="py-6 text-center space-y-2">
          <Icon className={cn(
            "h-8 w-8 mx-auto",
            isFeeIssue ? "text-amber-500" : isRestricted ? "text-destructive" : "text-muted-foreground"
          )} />
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">{reason}</p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
